import React from 'react';
import { Box, Grid, Menu, MenuItem, Typography } from '@mui/material';
import PluginAuthenticationModal from 'components/CreateSpark/SparkBuilderDrawers/RightDrawer/forms/PluginFunctions/PluginConnectionAuthentication/PluginAuthenticationModal';
import {
  DeleteIcon,
  DotsHorizontal,
  EditMenuIcon,
  ReconnectMenuIcon,
  TestMenuIcon,
} from 'components/SVG';
import { connectionStatusMap } from 'components/CreateSpark/SparkBuilderDrawers/RightDrawer/forms/PluginFunctions/PluginConnectionAuthentication/PluginConnectionAuthentication';
import { IconButtonWrapper } from 'sharedComponents/UI';
import { useDialog, usePopover } from 'hooks/common';
import {
  usePluginConnectionStatusTypes,
  usePluginConnectionTest,
} from 'hooks/SparkBuilder/pluginFunction';
import { useDeletePluginConnection } from 'hooks/SparkBuilder/pluginFunction/pluginConnectionsAndParameters/useDeletePluginConnection';
import type { Plugins } from 'types/Plugins';
import type { PluginConnections } from 'types/PluginConnections';
import ConfirmationDialog from 'components/common/ConfirmationDialog';

type ConnectionStatusMapType = React.Dispatch<
  React.SetStateAction<
    | {
        connectionStatus?: { type: string; message: string };
        isLoading: boolean;
        connectionID?: string;
      }
    | undefined
  >
>;

type ConnectionPopupProps = {
  selectedPlugin: Plugins | undefined;
  setIsEditing: React.Dispatch<React.SetStateAction<string | null>>;
  connection: PluginConnections;
  setConnectionStatusInfo: ConnectionStatusMapType;
};

const sortedMenuItems = [
  { label: 'Edit Name', action: 'edit', icon: <EditMenuIcon /> },
  { label: 'Test Connection', action: 'test', icon: <TestMenuIcon /> },
  { label: 'Reconnect', action: 'reconnect', icon: <ReconnectMenuIcon /> },
  {
    label: 'Delete',
    action: 'delete',
    icon: <DeleteIcon color='#000000DE' height='16' width='16' />,
  },
];

const modalTypes = {
  newConnection: 'newConnection',
  deleteConnection: 'deleteConnection',
  authentication: 'authentication',
} as const;

function ConnectionPopup({
  selectedPlugin,
  setIsEditing,
  connection,
  setConnectionStatusInfo,
}: ConnectionPopupProps) {
  const [modalType, setModalType] = React.useState<keyof typeof modalTypes>(
    modalTypes.newConnection
  );
  const { closeDialog, open: isDialogOpen, openDialog } = useDialog();

  const { anchorEl, open, openPopover, closePopover } = usePopover();

  const { mutateAsync: deletePluginConnection } = useDeletePluginConnection(
    selectedPlugin?.id
  );

  const {
    mutateAsync: testPluginConnection,
    isError: isTestPluginConnectionError,
    isSuccess: isTestPluginConnectionSuccess,
    isLoading: isTestPluginConnectionLoading,
  } = usePluginConnectionTest();

  const { pluginConnectionStatusTypeIDMap } = usePluginConnectionStatusTypes();

  const handleExpandMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    openPopover(event);
  };

  const testConnection = async () => {
    setConnectionStatusInfo({ isLoading: true, connectionID: connection.id });
    await testPluginConnection({
      pluginConnectionID: connection.id,
    });

    const pluginConnectionStatusType =
      pluginConnectionStatusTypeIDMap?.[
        connection.attributes.pluginConnectionStatusTypeID + ''
      ];

    const statusType = pluginConnectionStatusType?.attributes.name;
    const inActive = statusType === 'Unauthorized';

    if (inActive && !isTestPluginConnectionError) {
      setConnectionStatusInfo({
        connectionStatus: connectionStatusMap.inActive,
        isLoading: isTestPluginConnectionLoading,
        connectionID: connection.id,
      });
    }
    if (isTestPluginConnectionError) {
      setConnectionStatusInfo({
        connectionStatus: connectionStatusMap.error,
        isLoading: isTestPluginConnectionLoading,
        connectionID: connection.id,
      });
    }
    if (isTestPluginConnectionSuccess && !inActive) {
      setConnectionStatusInfo({
        connectionStatus: connectionStatusMap.success,
        isLoading: isTestPluginConnectionLoading,
        connectionID: connection.id,
      });
    }
  };

  const handleMenuItemClick = async (action: string) => {
    closePopover();

    switch (action) {
      case 'edit':
        setIsEditing(connection.id);
        return;
      case 'reconnect':
        openDialog();
        setModalType('authentication');
        return;
      case 'test':
        testConnection();
        return;
      case 'delete':
        openDialog();
        setModalType('deleteConnection');
    }
  };

  const deleteConnection = async () => {
    await deletePluginConnection(connection.id);
    closeDialog();
  };

  const onCreatePluginConnection = (connectionID: string) => {
    if (connectionID) {
      // setFieldValue('pluginConnectionID', connectionID);
    }
  };

  const handleClose = () => {
    closePopover();
  };

  return (
    <>
      <Grid item gap={1} container xs={1.5} justifyContent='flex-end'>
        <IconButtonWrapper
          onClick={handleExpandMenu}
          sx={{
            margin: 0,
            width: '32px',
            height: '32px',
          }}
        >
          <DotsHorizontal />
        </IconButtonWrapper>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {sortedMenuItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                handleMenuItemClick(item.action);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  aspectRatio: 1,
                  display: 'flex',
                  color: 'inherit',
                }}
              >
                {item.icon}
              </Box>
              <Typography component='span' variant='body1'>
                {item.label}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      </Grid>
      {isDialogOpen && modalType === 'authentication' && (
        <PluginAuthenticationModal
          isOpen={isDialogOpen}
          onClose={closeDialog}
          selectedPlugins={selectedPlugin}
          pluginAuthenticationID={connection.attributes.pluginAuthenticationID}
          pluginConnection={connection}
          onCreatePluginConnection={onCreatePluginConnection}
        />
      )}
      {isDialogOpen && modalType === 'deleteConnection' && (
        <ConfirmationDialog
          open={isDialogOpen}
          onClose={closeDialog}
          onPrimaryButtonClick={deleteConnection}
          hideSecondaryButton={false}
          primaryButtonText='Yes'
          secondaryButtonText='No'
          title={'Delete Connection?'}
          confirmationMessage={
            'Are you sure you want to permanently delete this connection?'
          }
        />
      )}
    </>
  );
}

export default ConnectionPopup;
